import React, { useEffect, useRef } from 'react';
import lottie from 'lottie-web';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import loadingAnimation from '../../assets/loading.json';

/**
 * MyBox Unified Animated Loader
 * Powered by MyBox dual-orbit particle animation (/var/www/smartlocker/loading.json)
 *
 * @param {string} variant - 'fullscreen' | 'overlay' | 'table' | 'page' | 'inline'
 * @param {number} size - width/height in px (default varies by variant)
 * @param {string} message - optional text under animation
 * @param {object} sx - additional MUI sx styles
 */
export default function MyBoxLoader({
  variant = 'inline',
  size,
  message,
  sx = {}
}) {
  const containerRef = useRef(null);
  const animInstance = useRef(null);

  // Default sizes based on variant
  const actualSize = size || (
    variant === 'fullscreen' ? 120 :
    variant === 'page' ? 85 :
    variant === 'table' || variant === 'overlay' ? 72 : 50
  );

  useEffect(() => {
    if (!containerRef.current) return;

    try {
      animInstance.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: loadingAnimation
      });
    } catch (e) {
      console.warn('[MyBoxLoader] failed to load Lottie animation:', e);
    }

    return () => {
      if (animInstance.current) {
        animInstance.current.destroy();
        animInstance.current = null;
      }
    };
  }, []);

  const animationNode = (
    <Box
      ref={containerRef}
      sx={{
        width: actualSize,
        height: actualSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    />
  );

  if (variant === 'fullscreen') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(248, 250, 252, 0.45)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
      >
        {animationNode}
      </Box>
    );
  }

  if (variant === 'overlay' || variant === 'table') {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(11, 15, 25, 0.70)' : 'rgba(255, 255, 255, 0.50)'),
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
      >
        {animationNode}
      </Box>
    );
  }

  if (variant === 'page') {
    return (
      <Box
        sx={{
          minHeight: '40vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
      >
        {animationNode}
      </Box>
    );
  }

  // Inline variant
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx
      }}
    >
      {animationNode}
    </Box>
  );
}
